/**
 * Keyboard shortcut registry.
 *
 * CLAUDE.md §11 asks for keyboard-first data entry, a global map, and a `?`
 * cheat sheet that lists it. Both need one source of truth, so shortcuts are
 * *registered* rather than bound ad hoc: `useShortcut()` adds an entry to a
 * module-level registry on mount and removes it on unmount, and the cheat sheet
 * simply renders whatever is currently registered. That also means the sheet can
 * never drift from the bindings that actually exist.
 *
 * One document-level `keydown` listener serves every binding; it is attached
 * when the first shortcut registers and detached when the last one leaves.
 */

import { computed, getCurrentScope, onScopeDispose, ref, unref, type ComputedRef, type Ref } from 'vue'

export type ShortcutGroup =
  | 'global'
  | 'navigation'
  | 'document'
  | 'grid'
  | 'list'
  | 'approvals'

export interface ShortcutDefinition {
  /** Stable id, unique per registration. */
  id: string
  /** Binding string, e.g. `mod+k`, `shift+?`, `alt+ArrowDown`. */
  keys: string
  /** Human sentence for the cheat sheet. Localised by the app. */
  description: string
  group: ShortcutGroup
}

export interface ShortcutOptions {
  /** Cheat-sheet metadata. Omit to bind without advertising the shortcut. */
  describe?: Omit<ShortcutDefinition, 'id' | 'keys'> & { id?: string }
  /** Reactive kill switch — a disabled shortcut stays registered but inert. */
  enabled?: Ref<boolean> | boolean
  /**
   * Fire while focus is inside a text input. Off by default so typing an `s`
   * into a description field never triggers Save.
   */
  allowInInput?: boolean
  /** Call `preventDefault()` when the binding matches. Defaults to true. */
  preventDefault?: boolean
  /** Listener target. Defaults to `document`. */
  target?: Document | HTMLElement
}

interface ParsedBinding {
  key: string
  mod: boolean
  ctrl: boolean
  meta: boolean
  alt: boolean
  shift: boolean
}

interface Registration {
  definition: ShortcutDefinition
  bindings: ParsedBinding[]
  handler: (event: KeyboardEvent) => void
  enabled: Ref<boolean> | boolean
  allowInInput: boolean
  preventDefault: boolean
}

/** The canonical global map from CLAUDE.md §11. Apps bind these by name. */
export const GLOBAL_SHORTCUTS = {
  commandPalette: 'mod+k',
  save: 'mod+s',
  submit: 'mod+enter',
  search: 'mod+/',
  quickCreate: 'mod+shift+n',
  cheatSheet: '?',
  closeOverlay: 'escape',
} as const satisfies Record<string, string>

export type GlobalShortcutName = keyof typeof GLOBAL_SHORTCUTS

/**
 * A plain Map plus a version counter rather than `reactive(new Map())`: a
 * reactive collection deep-unwraps the `Ref<boolean>` an `enabled` option
 * carries, which would silently turn a reactive kill switch into a snapshot.
 * Bumping `registryVersion` gives the cheat sheet its reactivity without that.
 */
const registrations = new Map<string, Registration>()
const registryVersion = ref(0)
const listeners = new Map<Document | HTMLElement, (event: KeyboardEvent) => void>()
const targetCounts = new Map<Document | HTMLElement, number>()

let idCounter = 0

function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

/** `mod` is Command on Apple hardware and Control everywhere else. */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const source = `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`
  return /Mac|iPhone|iPad|iPod/i.test(source)
}

const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  del: 'delete',
  ins: 'insert',
  space: ' ',
  spacebar: ' ',
  plus: '+',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  return: 'enter',
}

function normaliseKey(raw: string): string {
  const key = raw.trim().toLowerCase()
  return KEY_ALIASES[key] ?? key
}

/** Parse `mod+shift+k` into its modifier flags and base key. */
export function parseBinding(binding: string): ParsedBinding {
  const parsed: ParsedBinding = {
    key: '',
    mod: false,
    ctrl: false,
    meta: false,
    alt: false,
    shift: false,
  }

  // Split on '+' but keep a literal trailing '+' binding usable.
  const parts = binding.includes('++')
    ? [...binding.replace('++', '+').split('+').slice(0, -1), '+']
    : binding.split('+')

  for (const part of parts) {
    const token = normaliseKey(part)
    switch (token) {
      case 'mod':
      case 'cmdorctrl':
        parsed.mod = true
        break
      case 'ctrl':
      case 'control':
        parsed.ctrl = true
        break
      case 'meta':
      case 'cmd':
      case 'command':
        parsed.meta = true
        break
      case 'alt':
      case 'option':
        parsed.alt = true
        break
      case 'shift':
        parsed.shift = true
        break
      default:
        parsed.key = token
    }
  }

  return parsed
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag !== 'INPUT') return false
  const type = (target as HTMLInputElement).type
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file'].includes(type)
}

function matches(binding: ParsedBinding, event: KeyboardEvent): boolean {
  const eventKey = normaliseKey(event.key)
  if (binding.key !== '' && binding.key !== eventKey) return false

  const apple = isApplePlatform()
  const wantCtrl = binding.ctrl || (binding.mod && !apple)
  const wantMeta = binding.meta || (binding.mod && apple)

  if (event.ctrlKey !== wantCtrl) return false
  if (event.metaKey !== wantMeta) return false
  if (event.altKey !== binding.alt) return false

  // `?` already requires Shift on most layouts; do not demand it twice.
  if (binding.shift && !event.shiftKey) return false
  if (!binding.shift && event.shiftKey && binding.key.length === 1 && /[a-z]/.test(binding.key)) {
    return false
  }

  return true
}

function attach(target: Document | HTMLElement): void {
  const count = (targetCounts.get(target) ?? 0) + 1
  targetCounts.set(target, count)
  if (count > 1) return

  const listener = (event: KeyboardEvent): void => {
    if (event.defaultPrevented) return

    for (const registration of registrations.values()) {
      if (!unref(registration.enabled)) continue
      if (!registration.allowInInput && isTextEntry(event.target)) continue
      if (!registration.bindings.some((binding) => matches(binding, event))) continue

      if (registration.preventDefault) event.preventDefault()
      registration.handler(event)
      return
    }
  }

  listeners.set(target, listener)
  target.addEventListener('keydown', listener as EventListener)
}

function detach(target: Document | HTMLElement): void {
  const count = (targetCounts.get(target) ?? 1) - 1
  if (count > 0) {
    targetCounts.set(target, count)
    return
  }

  targetCounts.delete(target)
  const listener = listeners.get(target)
  if (listener) {
    target.removeEventListener('keydown', listener as EventListener)
    listeners.delete(target)
  }
}

export interface ShortcutHandle {
  id: string
  /** Remove the binding immediately. Idempotent. */
  stop: () => void
}

/**
 * Bind one or more key combinations for the lifetime of the calling scope.
 *
 * Registration is automatically torn down with the component (via
 * `onScopeDispose`); the returned handle lets a caller stop it early.
 */
export function useShortcut(
  keys: string | readonly string[],
  handler: (event: KeyboardEvent) => void,
  options: ShortcutOptions = {},
): ShortcutHandle {
  const list = typeof keys === 'string' ? [keys] : [...keys]
  const {
    describe,
    enabled = true,
    allowInInput = false,
    preventDefault = true,
    target = typeof document === 'undefined' ? undefined : document,
  } = options

  const id = describe?.id ?? nextId('shortcut')

  const registration: Registration = {
    definition: {
      id,
      keys: list.join(' / '),
      description: describe?.description ?? '',
      group: describe?.group ?? 'global',
    },
    bindings: list.map(parseBinding),
    handler,
    enabled,
    allowInInput,
    preventDefault,
  }

  let stopped = false
  const stop = (): void => {
    if (stopped) return
    stopped = true
    registrations.delete(id)
    registryVersion.value += 1
    if (target) detach(target)
  }

  registrations.set(id, registration)
  registryVersion.value += 1
  if (target) attach(target)

  if (getCurrentScope()) onScopeDispose(stop)

  return { id, stop }
}

/**
 * Everything currently bound *and* described, grouped for the `?` cheat sheet.
 * Undescribed bindings are intentionally hidden — they are implementation
 * details such as a grid's arrow-key navigation.
 */
export function useShortcutRegistry(): {
  all: ComputedRef<ShortcutDefinition[]>
  grouped: ComputedRef<{ group: ShortcutGroup; shortcuts: ShortcutDefinition[] }[]>
} {
  const all = computed<ShortcutDefinition[]>(() => {
    void registryVersion.value
    return [...registrations.values()]
      .map((registration) => registration.definition)
      .filter((definition) => definition.description !== '')
  })

  const grouped = computed<{ group: ShortcutGroup; shortcuts: ShortcutDefinition[] }[]>(() => {
    const buckets = new Map<ShortcutGroup, ShortcutDefinition[]>()
    for (const definition of all.value) {
      const bucket = buckets.get(definition.group)
      if (bucket) bucket.push(definition)
      else buckets.set(definition.group, [definition])
    }
    return [...buckets.entries()].map(([group, shortcuts]) => ({ group, shortcuts }))
  })

  return { all, grouped }
}

const SYMBOLS_APPLE: Record<string, string> = {
  mod: '⌘',
  meta: '⌘',
  ctrl: '⌃',
  alt: '⌥',
  shift: '⇧',
  enter: '↩',
  escape: 'Esc',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
}

const SYMBOLS_OTHER: Record<string, string> = {
  mod: 'Ctrl',
  meta: 'Win',
  ctrl: 'Ctrl',
  alt: 'Alt',
  shift: 'Shift',
  enter: 'Enter',
  escape: 'Esc',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
}

/** Render a binding for display, e.g. `mod+k` -> `⌘K` or `Ctrl+K`. */
export function formatBinding(binding: string, apple = isApplePlatform()): string {
  const table = apple ? SYMBOLS_APPLE : SYMBOLS_OTHER
  const separator = apple ? '' : '+'

  return binding
    .split('+')
    .map((part) => {
      const token = normaliseKey(part)
      const symbol = table[token]
      if (symbol) return symbol
      if (token === ' ') return 'Space'
      return token.length === 1 ? token.toUpperCase() : token.charAt(0).toUpperCase() + token.slice(1)
    })
    .join(separator)
}

/** Test seam: drop every registration and listener. */
export function resetShortcutRegistry(): void {
  registrations.clear()
  registryVersion.value += 1
  for (const [target, listener] of listeners) {
    target.removeEventListener('keydown', listener as EventListener)
  }
  listeners.clear()
  targetCounts.clear()
}

/** Number of live registrations — used by tests to assert teardown. */
export const shortcutCount = computed<number>(() => {
  void registryVersion.value
  return registrations.size
})

/** Convenience reactive flag some apps use to gate `mod`-prefixed hints. */
export const useAppleModifier = (): Ref<boolean> => ref(isApplePlatform())
